<template>
  <q-card class="hle-card">
    <q-card-section class="row items-center q-py-sm">
      <div class="text-h6">Positionner le texte et le bouton d'entrée</div>
      <q-space />
      <q-btn flat dense icon="close" v-close-popup />
    </q-card-section>
    <q-separator />

    <q-card-section class="hle-body">
      <p class="text-caption text-grey-7 q-mb-sm">
        Glissez le <b>texte d'accueil</b> et le <b>bouton d'entrée</b> à l'endroit voulu (poignée en bas à droite pour
        redimensionner). La position s'applique sur tous les modèles. Sans positionnement, chaque modèle garde sa mise en page par défaut.
      </p>

      <div class="hle-stage-wrap">
        <div ref="stageRef" class="hle-stage" :style="stageStyle">
          <!-- Bloc texte d'accueil -->
          <div class="hle-box hle-box--text" :style="boxStyle(text)" @mousedown="startDrag('text', $event)">
            <span class="hle-tag">Texte</span>
            <div class="hle-content" :style="{ whiteSpace: 'pre-line' }">{{ homeText }}</div>
            <span class="hle-resize" @mousedown.stop="startResize('text', $event)"></span>
          </div>

          <!-- Bloc bouton d'entrée -->
          <div class="hle-box hle-box--launch" :style="boxStyle(launch)" @mousedown="startDrag('launch', $event)">
            <span class="hle-tag">Bouton</span>
            <div class="hle-content hle-content--launch">
              <img v-if="launchImgSrc" :src="launchImgSrc" class="hle-launch-img" alt="" />
              <span>{{ launchText }}</span>
            </div>
            <span class="hle-resize" @mousedown.stop="startResize('launch', $event)"></span>
          </div>
        </div>
      </div>

      <div class="row items-center q-gutter-sm q-mt-sm">
        <q-btn flat dense color="grey-8" label="Réinitialiser ce placement"
          @click="resetLocal" />
        <q-space />
        <span v-if="saved" class="text-positive text-caption"><q-icon name="check" /> Position enregistrée</span>
        <span v-if="error" class="text-negative text-caption"><q-icon name="error" /> Échec de l'enregistrement</span>
      </div>
    </q-card-section>

    <q-separator />
    <q-card-actions align="right" class="q-pa-md">
      <q-btn flat label="Fermer" color="grey-8" v-close-popup />
      <q-btn unelevated color="secondary" label="Enregistrer la position" :loading="saving" @click="save" />
    </q-card-actions>

    <q-inner-loading :showing="loading"><q-spinner-gears size="40px" color="secondary" /></q-inner-loading>
  </q-card>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import axios from 'axios';

const BASE = process.env.VUE_APP_BASEURL;

// Placements par défaut (en % du viewport) si l'expo n'a pas encore de positionnement.
const DEFAULT_TEXT = { x: 20, y: 38, w: 60, h: 18 };
const DEFAULT_LAUNCH = { x: 42, y: 72, w: 16, h: 9 };

export default {
  props: {
    exhibitId: { required: true },
  },
  emits: ['saved'],
  setup(props, ctx) {
    const stageRef = ref(null);
    const text = ref({ ...DEFAULT_TEXT });
    const launch = ref({ ...DEFAULT_LAUNCH });
    const homeText = ref("Texte d'accueil");
    const launchText = ref('Entrer');
    const launchImgSrc = ref(null);
    const bgColor = ref('#ffffff');
    const bgImg = ref(null);
    const loading = ref(false);
    const saving = ref(false);
    const saved = ref(false);
    const error = ref(false);

    const stageStyle = computed(() => ({
      backgroundColor: bgColor.value,
      backgroundImage: bgImg.value ? `url(${bgImg.value})` : 'none',
    }));

    const boxStyle = (b) => ({
      left: `${b.x}%`, top: `${b.y}%`, width: `${b.w}%`, height: `${b.h}%`,
    });

    const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
    const round = (v) => Math.round(v * 10) / 10;

    let drag = null; // { which, kind, startX, startY, orig }
    const boxFor = (which) => (which === 'text' ? text.value : launch.value);

    const onMove = (e) => {
      if (!drag || !stageRef.value) return;
      const rect = stageRef.value.getBoundingClientRect();
      const dxPct = ((e.clientX - drag.startX) / rect.width) * 100;
      const dyPct = ((e.clientY - drag.startY) / rect.height) * 100;
      const b = boxFor(drag.which);
      if (drag.kind === 'move') {
        b.x = clamp(drag.orig.x + dxPct, 0, 100 - b.w);
        b.y = clamp(drag.orig.y + dyPct, 0, 100 - b.h);
      } else {
        b.w = clamp(drag.orig.w + dxPct, 5, 100 - b.x);
        b.h = clamp(drag.orig.h + dyPct, 4, 100 - b.y);
      }
    };
    const onUp = () => {
      drag = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    const startDrag = (which, e) => {
      e.preventDefault();
      const b = boxFor(which);
      drag = { which, kind: 'move', startX: e.clientX, startY: e.clientY, orig: { ...b } };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    };
    const startResize = (which, e) => {
      e.preventDefault();
      const b = boxFor(which);
      drag = { which, kind: 'resize', startX: e.clientX, startY: e.clientY, orig: { ...b } };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    };

    const resetLocal = async () => {
      // Efface le positionnement enregistré => l'accueil reprend le placement par défaut du modèle.
      try {
        await axios.delete(`${BASE}/exhibit_home/layout/${props.exhibitId}`);
        text.value = { ...DEFAULT_TEXT };
        launch.value = { ...DEFAULT_LAUNCH };
        saved.value = true;
        ctx.emit('saved');
        setTimeout(() => { saved.value = false; }, 1500);
      } catch (e) {
        console.log('HomeLayoutEditor.reset', e);
        error.value = true;
        setTimeout(() => { error.value = false; }, 1800);
      }
    };

    const load = async () => {
      loading.value = true;
      try {
        const { data } = await axios.get(`${BASE}/exhibit_home/${props.exhibitId}`);
        if (Array.isArray(data) && data.length) {
          const h = data[0];
          if (h.text) homeText.value = h.text;
          if (h.launch_text) launchText.value = h.launch_text;
          if (h.color) bgColor.value = h.color;
          if (h.text_x != null && h.text_y != null) {
            text.value = { x: h.text_x, y: h.text_y, w: h.text_width ?? DEFAULT_TEXT.w, h: h.text_height ?? DEFAULT_TEXT.h };
          }
          if (h.launch_x != null && h.launch_y != null) {
            launch.value = { x: h.launch_x, y: h.launch_y, w: h.launch_width ?? DEFAULT_LAUNCH.w, h: h.launch_height ?? DEFAULT_LAUNCH.h };
          }
          if (h.name) {
            try { const img = await axios.get(`${BASE}/s3Resource/${h.name}`); bgImg.value = img.data; } catch (e) { /* image facultative */ }
          }
        }
        try {
          const lr = await axios.get(`${BASE}/launchImage/${props.exhibitId}`);
          if (lr.data && lr.data[0] && lr.data[0].name) {
            const img = await axios.get(`${BASE}/s3Resource/${lr.data[0].name}`);
            launchImgSrc.value = img.data;
          }
        } catch (e) { /* bouton sans image */ }
      } catch (e) {
        console.log('HomeLayoutEditor.load', e);
      } finally {
        loading.value = false;
      }
    };

    const save = async () => {
      saving.value = true;
      try {
        await axios.put(`${BASE}/exhibit_home/layout`, {
          exhibitId: props.exhibitId,
          text_x: round(text.value.x), text_y: round(text.value.y),
          text_width: round(text.value.w), text_height: round(text.value.h),
          launch_x: round(launch.value.x), launch_y: round(launch.value.y),
          launch_width: round(launch.value.w), launch_height: round(launch.value.h),
        });
        saved.value = true;
        ctx.emit('saved');
        setTimeout(() => { saved.value = false; }, 1500);
      } catch (e) {
        console.log('HomeLayoutEditor.save', e);
        error.value = true;
        setTimeout(() => { error.value = false; }, 1800);
      } finally {
        saving.value = false;
      }
    };

    onMounted(load);
    onUnmounted(onUp);

    return {
      stageRef, text, launch, homeText, launchText, launchImgSrc,
      stageStyle, boxStyle, startDrag, startResize, resetLocal, save,
      loading, saving, saved, error,
    };
  },
};
</script>

<style scoped>
.hle-card { width: 100%; height: 100%; display: flex; flex-direction: column; }
.hle-body { padding-top: 12px; flex: 1 1 auto; min-height: 0; display: flex; flex-direction: column; }
.hle-stage-wrap { flex: 1 1 auto; min-height: 0; display: flex; justify-content: center; align-items: center; }
.hle-stage {
  position: relative;
  width: 100%;
  /* plafonne le stage par la hauteur dispo => tient dans l'écran sans scroll (16:9 conservé) */
  max-width: min(100%, calc((100vh - 260px) * 16 / 9));
  aspect-ratio: 16 / 9;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border: 1px solid #bdbdbd;
  overflow: hidden;
  user-select: none;
}
.hle-box {
  position: absolute;
  box-sizing: border-box;
  border: 2px dashed #26A69A;
  background: rgba(38, 166, 154, 0.18);
  -webkit-backdrop-filter: blur(2.5px);
  backdrop-filter: blur(2.5px);
  /* halo blanc (visible sur fond foncé) + ombre portée (visible sur fond clair) */
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.9), 0 2px 10px rgba(0, 0, 0, 0.45);
  cursor: move;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  min-height: 24px;
  overflow: hidden;
}
.hle-box--launch { border-color: #f57c00; background: rgba(245, 124, 0, 0.20); }
.hle-tag {
  position: absolute;
  top: 0; left: 0;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  padding: 3px 6px;
  background: #26A69A;
  color: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.45);
}
.hle-box--launch .hle-tag { background: #f57c00; }
.hle-content {
  padding: 6px 8px;
  font-size: 13px;
  font-weight: 600;
  color: #1d1d1f;
  text-align: center;
  pointer-events: none;
  overflow: hidden;
  /* halo blanc pour rester lisible sur n'importe quel fond */
  text-shadow: 0 0 4px #fff, 0 0 4px #fff, 0 1px 2px rgba(255, 255, 255, 0.95);
}
.hle-content--launch { display: flex; align-items: center; gap: 6px; }
.hle-launch-img { max-height: 28px; width: auto; object-fit: contain; }
.hle-resize {
  position: absolute;
  right: -1px; bottom: -1px;
  width: 16px; height: 16px;
  background: #fff;
  border: 2px solid #607d8b;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.9), 0 1px 4px rgba(0, 0, 0, 0.5);
  cursor: se-resize;
}
</style>
